import { useCallback, useRef, useState } from 'react';

import Popover, {
  InstanceRef,
} from '@decisiv/ui-components/lib/components/Popover';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import HighlightText from 'components/HighlightedText';
import { useSearchContext } from 'contexts/SearchContext';
import { Container } from 'pages/Teams/Cells/PlatformAccountsCell/styles';

type Props = {
  rowData: Pick<SentinelTeam, 'scheduledForDeletionAt'> & {
    platformAccounts: Pick<
      SentinelTeam['platformAccounts'][0],
      'id' | 'sourceId'
    >[];
  };
};

export default function AccountIdCell({
  rowData: { platformAccounts, scheduledForDeletionAt },
}: Props) {
  const actionRef = useRef(null);
  const [popover, setPopover] = useState<InstanceRef | null>(null);
  const popoverRef = useCallback(
    (instance: InstanceRef | null) => setPopover(instance),
    [],
  );
  const { searchString } = useSearchContext();

  const scheduledForDeletionProps = {
    ...(scheduledForDeletionAt && {
      palette: 'pastel' as const,
      color: 'licoriceMousse' as const,
      variant: undefined,
    }),
  };

  const renderAccountTag = (
    platformAccount: Props['rowData']['platformAccounts'][number],
  ) => (
    <Tag
      key={platformAccount.id}
      size="small"
      variant="outline"
      text={
        <HighlightText
          text={platformAccount.sourceId}
          searchText={searchString}
        />
      }
      {...scheduledForDeletionProps}
    />
  );

  if (platformAccounts.length <= 3) {
    return <Container>{platformAccounts.map(renderAccountTag)}</Container>;
  }

  return (
    <>
      <Container>
        <Tag
          size="small"
          variant="outline"
          actionAttributes={{
            'aria-label': `Show ${platformAccounts.length} account ids`,
          }}
          text={`+${platformAccounts.length}`}
          action={() => popover?.toggle()}
          actionRef={actionRef}
          actionIcon={ChevronDown}
          {...scheduledForDeletionProps}
        />
      </Container>
      <Popover
        ref={popoverRef}
        target={actionRef}
        manageEvents={false}
        placement="bottom-start"
        zIndex={3}
      >
        <Container
          flexDirection="column"
          padding={1}
          style={{ maxWidth: 'none', flexWrap: 'nowrap' }}
        >
          {platformAccounts.map(renderAccountTag)}
        </Container>
      </Popover>
    </>
  );
}
